import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    space, colors, fontSizes, lineHeights, fontWeights, shadows, mediaQueries
} from 'style/config';
import { wrapFunctionalComponent } from 'utils/framework';
import { getImageAltText } from 'utils/Accessibility';
import StringUtils from 'utils/String';
import {
    Grid, Box, Flex, Text, Button, Link, Divider
} from 'components/ui';
import ProductImage from 'components/Product/ProductImage/ProductImage';
import FlexIncentivesBanner from 'components/RichProfile/MyAccount/AutoReplenishment/FlexIncentivesBanner';
import AcceleratedPromoInfo from 'components/RichProfile/MyAccount/AutoReplenishment/SubscriptionItem/components/AcceleratedPromoInfo';
import Header from 'components/RichProfile/MyAccount/AutoReplenishment/SubscriptionItem/components/Header';
import { useSubscriptionItem } from 'components/RichProfile/MyAccount/AutoReplenishment/SubscriptionItem/hooks/useSubscriptionItem';

const SM_IMG_SIZE = 64;

/**
 * SubscriptionItem displays a single auto-replenishment subscription with product details,
 * pricing, delivery schedule, and subscription management actions.
 */
function SubscriptionItem({
    onOrderNowClick,
    firstName,
    setCurrentSubscription,
    subscription,
    toggleManageSubscriptionModal,
    toggleModifySubscriptionErrorModal,
    toggleResumeSubscriptionModal,
    toggleUnsubscribeModal
}) {
    const {
        featureFlags, hotDealData, localization, product, shouldShowItemVariation, subscriptionData
    } = useSubscriptionItem({
        subscription
    });

    const handleClickUnsubscribe = useCallback(() => {
        if (!subscription.isModifiable) {
            toggleModifySubscriptionErrorModal?.();
        } else {
            setCurrentSubscription?.(subscription);
            toggleUnsubscribeModal?.();
        }
    }, [subscription, setCurrentSubscription, toggleModifySubscriptionErrorModal, toggleUnsubscribeModal]);

    const handleManageSubscription = useCallback(() => {
        if (!subscription.isModifiable) {
            toggleModifySubscriptionErrorModal?.();
        } else {
            setCurrentSubscription?.(subscription);
            toggleManageSubscriptionModal?.();
        }
    }, [subscription, setCurrentSubscription, toggleModifySubscriptionErrorModal, toggleManageSubscriptionModal]);

    const handleResumeSubscription = useCallback(() => {
        setCurrentSubscription?.(subscription);
        toggleResumeSubscriptionModal?.();
    }, [subscription, setCurrentSubscription, toggleResumeSubscriptionModal]);

    const handleOrderNowClick = useCallback(() => {
        onOrderNowClick?.({ subscription });
    }, [onOrderNowClick, subscription]);

    return (
        <Flex
            css={styles.itemContainer}
            key={product.skuId}
        >
            <Header
                firstName={firstName}
                hotDealData={hotDealData}
                localization={localization}
                product={product}
                subscription={subscription}
                subscriptionData={subscriptionData}
            />

            <Divider />

            {featureFlags.isFlexIncentivesEnabled && !!subscription.flexIncentive && (
                <>
                    <FlexIncentivesBanner flexIncentive={subscription.flexIncentive} />
                    <Divider />
                </>
            )}

            <Grid css={styles.gridContainer}>
                <Box>
                    <ProductImage
                        id={product.skuId}
                        size={[SM_IMG_SIZE, 97]}
                        skuImages={product.skuImages}
                        disableLazyLoad={true}
                        altText={getImageAltText(product)}
                    />
                </Box>
                <Box>
                    <Grid
                        css={styles.content}
                        minHeight={[SM_IMG_SIZE, 0]}
                    >
                        <Box css={styles.contentLeft}>
                            <Text
                                is='p'
                                css={styles.title}
                                children={product.brandName}
                                data-at={Sephora.debug.dataAt('sku_brand')}
                            />
                            <Text
                                is='p'
                                css={styles.subtitle}
                                children={product.productName}
                                data-at={Sephora.debug.dataAt('sku_name')}
                            />
                            {shouldShowItemVariation && (
                                <Box css={styles.detailsContainer}>
                                    <Text
                                        is='p'
                                        css={styles.detailsText}
                                        data-at={Sephora.debug.dataAt('sku_size')}
                                    >
                                        {`${product.variationType}: ${product.variationValue}`}
                                        <span css={styles.detailsSeparator}>•</span>
                                        {StringUtils.format(localization.itemText, product.skuId)}
                                    </Text>
                                </Box>
                            )}
                            <Text
                                is='p'
                                data-at={Sephora.debug.dataAt('sku_qty')}
                            >
                                Qty: {product.qty}
                            </Text>
                            {subscriptionData.isActive && (
                                <Text
                                    css={styles.date}
                                    is='p'
                                    data-at={Sephora.debug.dataAt('sku_next_shipment')}
                                >
                                    {`${localization.nextShipment} ${subscriptionData.promiseDate}`}
                                </Text>
                            )}
                            {subscriptionData.isPaused && (
                                <Text
                                    css={styles.date}
                                    is='p'
                                >
                                    {`${localization.pausedDelivery} ${subscription.frequency} ${subscriptionData.frequencyDelivery}`}
                                </Text>
                            )}
                            {product.acceleratedPromotion && (
                                <AcceleratedPromoInfo
                                    localization={localization}
                                    hotDealData={hotDealData}
                                />
                            )}
                        </Box>
                        <Box css={styles.contentRight}>
                            <Box>
                                <Text
                                    is='p'
                                    css={styles.salePrice}
                                    data-at={Sephora.debug.dataAt('sku_discounted_price')}
                                >
                                    {product.savingAmount}
                                    {product.acceleratedPromotion &&
                                        !hotDealData.acceleratedDiscountReclaimPossible &&
                                        !hotDealData.isPromoExpirationSoon &&
                                        '*'}
                                </Text>
                                <Text
                                    is='del'
                                    css={styles.listPrice}
                                    data-at={Sephora.debug.dataAt('sku_price')}
                                >
                                    {product.productPrice}
                                </Text>
                            </Box>
                        </Box>
                    </Grid>
                    <Box css={[styles.ctaContainer, featureFlags.isOrderNowEnabled && styles.ctaContainerOrderNow]}>
                        <Button
                            size='sm'
                            variant='secondary'
                            css={styles.secondaryCta}
                            onClick={subscriptionData.isPaused ? handleResumeSubscription : handleManageSubscription}
                        >
                            {subscriptionData.isPaused ? localization.resumeSubscription : localization.manageSubscription}
                        </Button>

                        {featureFlags.isOrderNowEnabled && onOrderNowClick && (
                            <Button
                                size='sm'
                                variant='secondary'
                                css={styles.secondaryCta}
                                onClick={handleOrderNowClick}
                            >
                                {localization.getItNow}
                            </Button>
                        )}
                        {!featureFlags.isOrderNowEnabled && (
                            <Link
                                css={styles.blueLink}
                                onClick={handleClickUnsubscribe}
                            >
                                {localization.unsubscribe}
                            </Link>
                        )}
                    </Box>
                </Box>
            </Grid>
        </Flex>
    );
}

const styles = {
    itemContainer: {
        borderRadius: space[2],
        boxShadow: shadows.light,
        flexDirection: 'column',
        gap: space[3],
        marginTop: space[4],
        padding: `0 ${space[4]}px ${space[1]}px ${space[4]}px`
    },
    gridContainer: {
        gridTemplateColumns: 'auto 1fr',
        padding: `${space[3]}px 0`,
        lineHeight: lineHeights.tight,
        alignItems: 'start',
        gap: space[1],
        [mediaQueries.sm]: {
            columnGap: space[2]
        }
    },
    content: {
        gap: space[2],
        gridTemplateColumns: '1fr auto'
    },
    contentLeft: {
        padding: space[2]
    },
    title: {
        fontSize: fontSizes.base,
        fontWeight: fontWeights.bold
    },
    subtitle: {
        fontSize: fontSizes.base,
        marginBottom: space[1],
        lineHeight: lineHeights.tight
    },
    detailsContainer: {
        color: colors.gray
    },
    detailsText: {
        display: 'inline-block',
        lineHeight: lineHeights.tight
    },
    detailsSeparator: {
        marginLeft: space[2],
        marginRight: space[2]
    },
    date: {
        marginTop: space[2],
        whiteSpace: 'nowrap'
    },
    contentRight: {
        textAlign: 'right'
    },
    salePrice: {
        fontWeight: fontWeights.bold,
        marginTop: space[2],
        color: colors.red
    },
    listPrice: {
        color: colors.black
    },
    ctaContainer: {
        marginTop: space[4],
        display: 'flex',
        flexWrap: 'wrap',
        rowGap: space[2],
        columnGap: space[4],
        alignItems: 'center'
    },
    ctaContainerOrderNow: {
        columnGap: space[3],
        [mediaQueries.sm]: {
            columnGap: space[2]
        }
    },
    secondaryCta: {
        borderWidth: '1px',
        fontWeight: 'normal'
    },
    blueLink: {
        color: colors.blue,
        fontSize: fontSizes.base
    }
};

SubscriptionItem.propTypes = {
    onOrderNowClick: PropTypes.func,
    toggleManageSubscriptionModal: PropTypes.func,
    toggleModifySubscriptionErrorModal: PropTypes.func,
    toggleUnsubscribeModal: PropTypes.func,
    toggleResumeSubscriptionModal: PropTypes.func,
    setCurrentSubscription: PropTypes.func,
    subscription: PropTypes.shape({
        isModifiable: PropTypes.bool,
        frequency: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        status: PropTypes.string,
        frequencyType: PropTypes.string,
        nextScheduleRunDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
        items: PropTypes.arrayOf(
            PropTypes.shape({
                skuId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                brandName: PropTypes.string,
                productName: PropTypes.string,
                skuImages: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
                price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                qty: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                discountAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                discountedPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                discountType: PropTypes.string,
                yearlySavings: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                acceleratedPromotion: PropTypes.shape({
                    remainingOrderCount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                    remainingAcceleratedDiscount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                    promoExpirationDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
                    discountAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                    acceleratedDiscountReclaimPossible: PropTypes.bool
                })
            })
        )
    }).isRequired,
    firstName: PropTypes.string.isRequired
};

export default wrapFunctionalComponent(SubscriptionItem, 'SubscriptionItem');
